/**
 * ChatTranscript — reusable iMessage-style transcript bubble list.
 *
 * Used by Recruiters page (recruiter group chat) and CreatorDetail (member
 * conversations). The component is presentational only — callers pass items
 * and decide where to fetch them from.
 */
import * as React from "react";
import { Skeleton } from "@/components/ui/skeleton";
import { Empty } from "@/components/ui/empty";

export interface ChatBubbleItem {
  id: string;
  /** "outbound" = from us / Kitt, rendered left in blue. "inbound" = from
   * them, rendered right in gray. */
  direction: "inbound" | "outbound";
  /** Free-form short label shown above the bubble (e.g. "Kitt", "Alexandra",
   * "Justin", "owner"). Optional. */
  label?: string | null;
  /** Optional secondary tag — usually `ai_intent` like `daily_checkin` or
   * `manual_launch_log`. Renders next to label. */
  tag?: string | null;
  /** ISO timestamp. Renders the HH:MM next to the label. */
  timestamp?: string | null;
  /** The message text. */
  text: string | null;
}

export interface ChatTranscriptProps {
  items: ChatBubbleItem[];
  loading?: boolean;
  /** Empty-state title when items.length === 0 */
  emptyTitle?: string;
  emptyHint?: string;
  /** Tailwind classes to override the wrapper height. Default: max-h-80. */
  heightClass?: string;
}

export function ChatTranscript({
  items, loading,
  emptyTitle = "No messages yet",
  emptyHint = "Inbound and outbound messages will appear here.",
  heightClass = "max-h-80",
}: ChatTranscriptProps) {
  if (loading) return <Skeleton className="h-32 w-full" />;
  if (items.length === 0)
    return <Empty title={emptyTitle} hint={emptyHint} />;
  return (
    <div className={`space-y-2 overflow-y-auto rounded-md border p-3 bg-muted/30 ${heightClass}`}>
      {items.map((m) => {
        const isOut = m.direction === "outbound";
        const time = m.timestamp ? m.timestamp.slice(11, 16) : null;
        const label = m.label || (isOut ? "Kitt" : "?");
        return (
          <div key={m.id} className={"flex " + (isOut ? "justify-start" : "justify-end")}>
            <div
              className={
                "max-w-[75%] rounded-2xl px-3 py-2 text-sm " +
                (isOut
                  ? "bg-blue-100 text-blue-900"
                  : "bg-zinc-200 text-zinc-900")
              }
            >
              <div className="text-[10px] uppercase opacity-60 mb-0.5">
                {label}
                {m.tag ? ` · ${m.tag}` : ""}
                {time ? ` · ${time}` : ""}
              </div>
              <div className="whitespace-pre-wrap">{m.text}</div>
            </div>
          </div>
        );
      })}
    </div>
  );
}
